/** @jsxImportSource @emotion/react */
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import moment from 'moment'
import ReactSelect from 'react-select'

import DashboardContent from '../atoms/DashboardContent'
import DataTable from 'components/atoms/DataTable'
import { TableWrapper } from 'components/atoms/TableWrapper'
import Select from 'components/atoms/Select'
import Input from 'components/atoms/Input'
import Button from 'components/atoms/Button'
import routes from '../constants/routes'
import { useGetRetellOutboundActivityQuery } from 'store/services/retellOutboundActivity'
import Toast from 'components/atoms/Toast'
import { UserPermission } from 'components/lab/user/permissions'
import { useLabPermission } from 'hooks/useLabPermission'
import {
  useGetRetellOutboundSettingsQuery,
  useUpdateRetellOutboundSettingsMutation,
} from 'store/services/retellOutboundSettings'

const PAGE_SIZE = 20
const POLL_MS = 20000

const STATUS_BADGE: Record<string, string> = {
  pending: 'badge-secondary',
  simulated: 'badge-info',
  created: 'badge-primary',
  completed: 'badge-success',
  skipped: 'badge-warning',
  failed: 'badge-danger',
  writeback_failed: 'badge-danger',
  creating: 'badge-secondary',
}

const STATUS_OPTIONS = [
  { value: '', name: 'All statuses' },
  { value: 'pending', name: 'Pending' },
  { value: 'simulated', name: 'Simulated' },
  { value: 'created', name: 'Created' },
  { value: 'completed', name: 'Completed' },
  { value: 'skipped', name: 'Skipped' },
  { value: 'failed', name: 'Failed' },
  { value: 'writeback_failed', name: 'Writeback Failed' },
]

const TRANSFERRED_OPTIONS = [
  { value: '', name: 'All calls' },
  { value: 'true', name: 'Transferred' },
  { value: 'false', name: 'Not transferred' },
]

const BUCKET_OPTIONS = [
  { value: 'HST_PATIENT_SCHEDULING', label: 'HST patient scheduling' },
  { value: 'HST_PATIENT_TESTING', label: 'HST patient testing' },
  { value: 'HST_PENDING_RETURN', label: 'HST pending return' },
  { value: 'POX_DELIVERED', label: 'POX delivered' },
  { value: 'POX_PENDING_RETURN', label: 'POX pending return' },
]

type SortState = { sortBy: 'createdAt' | 'scheduledFor'; sortDir: 'asc' | 'desc' }

const RetellOutboundActivity = () => {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [bucketFilter, setBucketFilter] = useState<string[]>([])
  const [transferredFilter, setTransferredFilter] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [sort, setSort] = useState<SortState>({ sortBy: 'createdAt', sortDir: 'desc' })
  const [currentPage, setCurrentPage] = useState(0)
  const [pageSize, setPageSize] = useState(PAGE_SIZE)

  const { data, isFetching } = useGetRetellOutboundActivityQuery(
    {
      ...(search && { search }),
      ...(statusFilter && { status: statusFilter }),
      ...(bucketFilter.length > 0 && { buckets: bucketFilter }),
      ...(transferredFilter !== '' && { transferred: transferredFilter === 'true' }),
      ...(fromDate && { fromDate }),
      ...(toDate && { toDate }),
      sortBy: sort.sortBy,
      sortDir: sort.sortDir,
      page: currentPage,
      size: pageSize,
    },
    { pollingInterval: POLL_MS },
  )

  const canManagePause = useLabPermission(UserPermission.MANAGE_RETELL_OUTBOUND_SETTINGS)
  const { data: settings } = useGetRetellOutboundSettingsQuery()
  const [updateSettings, { isLoading: togglingPause }] = useUpdateRetellOutboundSettingsMutation()

  const togglePause = async () => {
    const wasPaused = settings?.paused ?? false
    try {
      await updateSettings({ paused: !wasPaused }).unwrap()
      Toast({ type: 'success', label: wasPaused ? 'Outbound calling resumed.' : 'Outbound calling paused.' })
    } catch {
      Toast({ type: 'error', label: 'Failed to update pause state.' })
    }
  }

  const handleSort = (sortBy?: any) => {
    // DataTable re-invokes this during the page-change cycle, so only react to a
    // genuine sort change — otherwise we'd reset the page on every pagination click.
    const next: SortState = sortBy
      ? {
          sortBy: sortBy.id === 'scheduledFor' ? 'scheduledFor' : 'createdAt',
          sortDir: sortBy.desc ? 'desc' : 'asc',
        }
      : { sortBy: 'createdAt', sortDir: 'desc' }
    if (next.sortBy === sort.sortBy && next.sortDir === sort.sortDir) {
      return
    }
    setCurrentPage(0)
    setSort(next)
  }

  const headers = useMemo(
    () => [
      {
        Header: 'Created',
        accessor: 'createdAt',
        Cell: ({ row }: any) => moment(row.original.createdAt).format('MM/DD HH:mm'),
      },
      {
        Header: 'Scheduled',
        accessor: 'scheduledFor',
        Cell: ({ row }: any) =>
          row.original.scheduledFor ? moment(row.original.scheduledFor).format('MM/DD HH:mm') : '—',
      },
      {
        Header: 'Order',
        accessor: 'orderId',
        disableSortBy: true,
        Cell: ({ row }: any) => (
          <Link to={`${routes.index}${routes.order.edit}${row.original.orderId}`}>
            {row.original.orderId}
          </Link>
        ),
      },
      {
        Header: 'Patient',
        accessor: 'patientName',
        disableSortBy: true,
        Cell: ({ row }: any) => row.original.patientName || '—',
      },
      { Header: 'Bucket', accessor: 'bucket', disableSortBy: true },
      { Header: 'Reason', accessor: 'callReason', disableSortBy: true },
      {
        Header: 'Status',
        accessor: 'status',
        disableSortBy: true,
        Cell: ({ row }: any) => (
          <span className={`badge ${STATUS_BADGE[row.original.status] || 'badge-secondary'}`}>
            {row.original.status}
          </span>
        ),
      },
      {
        Header: 'Transferred',
        accessor: 'transferred',
        disableSortBy: true,
        Cell: ({ row }: any) =>
          row.original.transferred ? <span className="badge badge-success">Transferred</span> : '—',
      },
      {
        Header: 'Decision',
        accessor: 'suppressionReason',
        disableSortBy: true,
        Cell: ({ row }: any) => {
          const reason = row.original.suppressionReason || '—'
          const count = row.original.deferralCount
          return count > 0
            ? `${reason} (deferred ×${count}${row.original.lastDeferralReason ? `: ${row.original.lastDeferralReason}` : ''})`
            : reason
        },
      },
      {
        Header: 'Retell Call',
        accessor: 'retellCallId',
        disableSortBy: true,
        Cell: ({ row }: any) => row.original.retellCallId || '—',
      },
      {
        Header: 'Recording',
        accessor: 'recordingUrl',
        disableSortBy: true,
        Cell: ({ row }: any) =>
          row.original.recordingUrl ? (
            <audio controls src={row.original.recordingUrl} css={{ height: '30px', maxWidth: '220px' }} />
          ) : (
            '—'
          ),
      },
    ],
    [],
  )

  return (
    <DashboardContent
      icon="fas fa-phone"
      title="AI Outbound Calls"
      subtitle="Retell outbound activity (planned, simulated, and performed calls)"
      content={
        <div className="card card-primary">
          <div className="card-body">
            <div className="d-flex justify-content-end align-items-center mx-4 mt-2" css={{ gap: '0.5rem' }}>
              {settings?.paused && (
                <span className="badge badge-danger" css={{ fontSize: '0.85rem' }}>Outbound calling paused</span>
              )}
              <Button
                label={togglingPause ? 'Saving…' : settings?.paused ? 'Resume calling' : 'Pause calling'}
                className={`btn-sm ${settings?.paused ? 'btn-success' : 'btn-default'}`}
                disabled={!canManagePause || togglingPause}
                onClick={togglePause}
              >
                <i className={`fas fa-${settings?.paused ? 'play' : 'pause'} mr-1`} />
              </Button>
            </div>
            <div className="row px-4 mb-3" css={{ gap: '1rem', alignItems: 'flex-end' }}>
              <div css={{ minWidth: '240px' }}>
                <Input
                  value={search}
                  label="Search"
                  placeholder="Order ID or patient name"
                  name="search"
                  handleChange={(e) => {
                    setCurrentPage(0)
                    setSearch(e.target.value)
                  }}
                />
              </div>
              <div css={{ minWidth: '200px' }}>
                <Select
                  name="status"
                  label="Status"
                  value={statusFilter}
                  options={STATUS_OPTIONS}
                  noDefault
                  wrapperClassName="mb-0"
                  handleChange={(e) => {
                    setCurrentPage(0)
                    setStatusFilter(e.target.value)
                  }}
                />
              </div>
              <div css={{ minWidth: '280px' }}>
                <label className="text-gray">Buckets</label>
                <ReactSelect
                  isMulti
                  options={BUCKET_OPTIONS}
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  value={BUCKET_OPTIONS.filter((option) => bucketFilter.includes(option.value))}
                  onChange={(selected) => {
                    setCurrentPage(0)
                    setBucketFilter((selected ?? []).map((option) => option.value))
                  }}
                />
              </div>
              <div css={{ minWidth: '180px' }}>
                <Select
                  name="transferred"
                  label="Transferred"
                  value={transferredFilter}
                  options={TRANSFERRED_OPTIONS}
                  noDefault
                  wrapperClassName="mb-0"
                  handleChange={(e) => {
                    setCurrentPage(0)
                    setTransferredFilter(e.target.value)
                  }}
                />
              </div>
              <div css={{ minWidth: '160px' }}>
                <Input
                  type="date"
                  value={fromDate}
                  label="Scheduled from"
                  name="fromDate"
                  maxValue={toDate || undefined}
                  handleChange={(e) => {
                    setCurrentPage(0)
                    setFromDate(e.target.value)
                  }}
                />
              </div>
              <div css={{ minWidth: '160px' }}>
                <Input
                  type="date"
                  value={toDate}
                  label="Scheduled to"
                  name="toDate"
                  minValue={fromDate || undefined}
                  handleChange={(e) => {
                    setCurrentPage(0)
                    setToDate(e.target.value)
                  }}
                />
              </div>
              <Button
                label="Clear"
                className="btn-default"
                onClick={() => {
                  setSearch('')
                  setStatusFilter('')
                  setBucketFilter([])
                  setTransferredFilter('')
                  setFromDate('')
                  setToDate('')
                  setSort({ sortBy: 'createdAt', sortDir: 'desc' })
                  setCurrentPage(0)
                }}
              />
            </div>
            <div className="container-fluid">
              <TableWrapper className="card-body row">
                <DataTable
                  tableKey="retell_outbound_activity"
                  data={data ?? { items: [], currentPage: 0, totalItems: 0 }}
                  status={isFetching}
                  tableColumns={headers}
                  pageSize={pageSize}
                  setCurrentPage={setCurrentPage}
                  setPageSize={setPageSize}
                  setSortOptions={handleSort}
                  entityName="attempts"
                  searchable={false}
                />
              </TableWrapper>
            </div>
          </div>
        </div>
      }
      breadcrumb={[{ label: 'AI Outbound Calls', to: `${routes.index}${routes.retellOutboundActivity.root}` }]}
    />
  )
}

export default RetellOutboundActivity
